<template>
  <!-- Hero Start -->
  <section
    id="home"
    class="relative md:pt-40 md:pb-32 pt-28 pb-20 table w-full items-center bg-center overflow-hidden"
  >
    <!-- Hero poster as explicit LCP candidate. Single <picture> element
         (not two <img>, not NuxtImg) so the browser only fetches the source
         that matches the viewport — PSI 2026-05-02 19:07 trace showed two
         separate <img> tags both fetched their src on mobile (the
         display:none "hidden" class doesn't suppress <img> network load),
         wasting 184 KB on the desktop poster. <picture>+<source media>
         is the spec-correct way: only the matching source fires.
         Plain HTML refs (no IPX) so URLs match the rel=preload links in
         useHead — IPX would rewrite to /_ipx/... and the preload would miss.
         Meaningful alt required: alt="" makes Lighthouse 13 skip the
         element as decorative (PSI 17:08 → 17:22 confirmed). -->
    <picture>
      <source
        media="(min-width: 1280px)"
        srcset="/videos/DreamLand-small-v2.webp"
      >
      <source
        media="(min-width: 768px) and (max-width: 1279px)"
        srcset="/images/complex/complex2-tablet-v2.webp"
      >
      <source
        media="(max-width: 767px)"
        srcset="/images/complex/complex2-mobile-v3.webp"
      >
      <img
        class="absolute inset-0 h-full w-full object-cover"
        src="/images/complex/complex2-mobile-v3.webp"
        width="1200"
        height="438"
        loading="eager"
        fetchpriority="high"
        decoding="sync"
        :alt="$t('hero.poster_alt')"
      >
    </picture>

    <HeroMobileSlideshow v-if="showSlideshow" />

    <!-- Desktop background video (≥1024 px), mounted only after the browser
         is idle on the client. SSR HTML contains the poster <picture> alone,
         so Chromium's LCP algorithm picks it up unambiguously — no race with
         a <video> covering it. After idle on desktop the video appears,
         autoplays, and covers the poster — but by then the LCP timestamp
         has been captured. Lighthouse 13's window stays open longer than
         the previous 1.5 s heuristic, so the timeout is 5 s. -->
    <video
      v-if="showVideo"
      class="absolute inset-0 h-full w-full object-cover"
      autoplay
      muted
      loop
      playsinline
      preload="metadata"
    >
      <source src="/videos/DreamLand-mid.mp4" type="video/mp4" media="(max-width: 1279px)" />
      <!-- desktop.mp4 is the optimised hero loop (1080p, 2.5 Mbps H.264, no
           audio, faststart) — ~10 MB vs full.mp4's 33 MB. full.mp4 is kept in
           the repo as the untouched master for future re-encodes; do NOT wire
           it into the page. -->
      <source src="/videos/DreamLand-desktop.mp4" type="video/mp4" />
      <!-- Empty WebVTT track satisfies Lighthouse "video has captions"
           audit; the loop is a decorative background, no spoken content. -->
      <track kind="captions" srclang="en" label="No captions" src="/captions/empty.vtt" default>
    </video>

    <!-- Gradient overlay for WCAG AA contrast on white text over photo.
         /55 → /45 keeps the poster <img> visible enough for Chromium's
         LCP "obscured" heuristic. Contrast lives on per-element
         text-shadow (H1 + slogan + TrustEyebrow + datepicker inputs). -->
    <div
      class="absolute inset-0 bg-gradient-to-b from-slate-900/55 to-slate-900/45"
    ></div>

    <div class="container relative z-10">
      <div class="grid grid-cols-1">
        <div class="text-center">
          <TrustEyebrow variant="hero" />
          <h1
            class="font-display italic text-white lg:leading-tight leading-normal text-4xl sm:text-5xl lg:text-7xl mb-6 mt-5 hero-text"
          >
            {{ $t("hero.title") }}
          </h1>
          <p
            class="text-white text-xl font-body font-light tracking-wide max-w-xl mx-auto hero-text"
          >
            {{ $t("hero.slogan") }}
          </p>

          <!-- Search Form -->
          <div class="mt-10">
            <BookingSearchForm theme="hero-glass" />
          </div>
        </div>
      </div>
    </div>
  </section>
  <!-- Hero End -->
</template>

<script setup lang="ts">
import { ref, onMounted } from "vue";
import { shouldMountSlideshow, HERO_MOUNT_DELAY_MS } from "~/utils/heroSlideshow";
// Explicit import: Nuxt's path-prefixed auto-import would name this
// `MainpageHeroMobileSlideshow`, so the bare `<HeroMobileSlideshow>` tag would
// fail to resolve. Importing directly keeps the tag working regardless.
import HeroMobileSlideshow from "./HeroMobileSlideshow.vue";

// Desktop video is mounted client-side AFTER the browser is idle so the
// poster <img> stays the unambiguous LCP candidate. SSR has no <video> in
// the DOM at all → on mobile (<1024 px) the element never appears, so the
// 5.7 MiB / 32 MiB sources are never fetched.
const showVideo = ref(false);
const showSlideshow = ref(false);

// Preload poster images: desktop and mobile. Plain HTML refs (no IPX) so
// the URL matches what's served, otherwise the preload misses. Each `media`
// MUST mirror the matching <source media> exactly so the browser only
// downloads the poster the layout will actually use. Tablet tier (768-1279)
// is intentionally not preloaded — <source> selection at parse time is
// fast enough for a 67 KiB image.
useHead({
  link: [
    {
      rel: "preload",
      as: "image",
      href: "/images/complex/complex2-mobile-v3.webp",
      fetchpriority: "high",
      media: "(max-width: 767px)",
    },
    {
      rel: "preload",
      as: "image",
      href: "/videos/DreamLand-small-v2.webp",
      fetchpriority: "high",
      media: "(min-width: 1280px)",
    },
  ],
});

function scheduleAfterIdle(cb: () => void, timeout: number) {
  const ric = (window as Window & {
    requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
  }).requestIdleCallback;
  if (typeof ric === "function") ric(cb, { timeout });
  else setTimeout(cb, timeout);
}

onMounted(() => {
  const isMobile = window.matchMedia("(max-width: 767px)").matches;
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  // Mobile (<=767px): mount the Ken Burns slideshow after the LCP window.
  // Unlike the desktop video (video frames are not LCP candidates), the
  // slideshow slides are <img> that CAN become the LCP element — a fading,
  // Ken-Burns-scaling image is exactly what produced the old 17.4s regression.
  // So use a HARD setTimeout floor, not requestIdleCallback (which fires on the
  // first idle, often <1s — too early, inside Lighthouse's LCP window).
  if (isMobile) {
    if (shouldMountSlideshow({ isMobile, reducedMotion })) {
      setTimeout(() => { showSlideshow.value = true; }, HERO_MOUNT_DELAY_MS);
    }
    return;
  }

  // 768-1023px: static poster only (no video, no slideshow) — unchanged.
  if (window.matchMedia("(max-width: 1023px)").matches) return;

  // Desktop (>=1024px): mount the background video after idle so the poster
  // <img> wins LCP cleanly (Lighthouse 13 window ~5s).
  const VIDEO_MOUNT_DELAY_MS = 5000;
  scheduleAfterIdle(() => { showVideo.value = true; }, VIDEO_MOUNT_DELAY_MS);
});
</script>

<style>
/* Per-element shadow for white hero text. Lifts contrast against bright
   bottom-half photos (pool, beach) without darkening the gradient overlay,
   which would push the underlying LCP poster image past Chromium's
   "obscured" threshold and trigger NO_LCP. Two-layer shadow: tight 1-px
   for crisp legibility + wider 12-px halo for body of letters. */
.hero-text {
  text-shadow:
    0 1px 3px rgba(0, 0, 0, 0.6),
    0 2px 12px rgba(0, 0, 0, 0.45);
}
</style>
